/**
 * Explicit "save for offline" registry for Work Orders.
 *
 * The service worker already caches whitelisted read-only GETs on a
 * last-seen basis (see `public/service-worker.js`). This module sits on
 * top of that to give the user a one-tap guarantee: "I need this job
 * brief in my pocket — even if I've never opened it and I'm going where
 * there's no signal."
 *
 * Mechanism is simple: we force-fetch the WO detail endpoint + every
 * attachment URL, which the SW then writes into `lutrom-api-v1` and
 * `lutrom-attachments-v1` respectively. The localStorage registry is
 * only a UI hint — it tells the pin button "show filled / show empty" —
 * the real truth lives in the browser's CacheStorage.
 */

const STORAGE_KEY = 'lutrom.wo.offline-pinned';

const safeRead = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch { return []; }
};

const safeWrite = (ids) => {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(ids)); } catch { /* private mode */ }
};

export const getPinnedIds = () => new Set(safeRead());

export const isWorkOrderPinned = (id) => safeRead().includes(id);

/**
 * Build the list of URLs we need to warm-cache for a given WO. Uses the
 * same `REACT_APP_BACKEND_URL` base as the rest of the app so the URLs
 * stay same-origin with the SW scope (anything cross-origin wouldn't be
 * cacheable anyway).
 */
const urlsForWo = (wo) => {
  const backend = process.env.REACT_APP_BACKEND_URL || '';
  const urls = [`${backend}/api/work-orders/${wo.id}`];
  for (const a of (wo.attachments || [])) {
    if (a && a.url) urls.push(`${backend}${a.url}`);
  }
  return urls;
};

/**
 * Pin this Work Order for offline. Fetches the detail + every attachment
 * so the SW populates its caches, then records the id in localStorage.
 *
 * Resolves to true on success, false if any fetch failed (the SW may
 * still have picked up the ones that succeeded). We don't throw — this
 * is a best-effort "pre-cache" action; if the user is already offline
 * we still want to flip the registry so the button state is accurate.
 */
export const pinWorkOrder = async (wo) => {
  if (!wo || !wo.id) return false;
  const ids = safeRead();
  if (!ids.includes(wo.id)) {
    ids.push(wo.id);
    safeWrite(ids);
  }
  const urls = urlsForWo(wo);
  const results = await Promise.allSettled(
    urls.map(u => fetch(u, { credentials: 'include' })),
  );
  return results.every(r => r.status === 'fulfilled' && r.value.ok);
};

/**
 * Unpin this Work Order. Removes the id from the registry AND asks the
 * SW to drop its cached attachments so we don't waste disk on photos
 * the user explicitly said they don't need offline anymore. The API
 * cache is shared with other auto-cached pages, so we leave that alone.
 */
export const unpinWorkOrder = (wo) => {
  if (!wo || !wo.id) return;
  const next = safeRead().filter(i => i !== wo.id);
  safeWrite(next);
  const urls = (wo.attachments || [])
    .filter(a => a && a.url)
    .map(a => `${process.env.REACT_APP_BACKEND_URL || ''}${a.url}`);
  if (urls.length && navigator.serviceWorker?.controller) {
    navigator.serviceWorker.controller.postMessage({
      type: 'purge-attachments',
      urls,
    });
  }
};
